import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Trophy, RotateCcw, Brain, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface QuizOption {
  label: string;
  text: string;
}

interface QuizQuestion {
  questionNumber: number;
  question: string;
  options: QuizOption[];
  correctAnswer?: string;
  explanation?: string;
}

interface QuizData {
  slug: string;
  title: string;
  description: string;
  totalQuestions: number;
  attemptCount: number;
  questions: QuizQuestion[];
}

interface QuizComponentProps {
  quizSlug: string;
}

const QuizComponent: React.FC<QuizComponentProps> = ({ quizSlug }) => {
  const { state } = useAuth();
  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // localStorage key for this quiz
  const storageKey = `quiz-progress-${quizSlug}`;

  // Quiz state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [startTime, setStartTime] = useState<number>(0);

  // Per-question state
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answered, setAnswered] = useState(false);

  // Results tracking
  const [results, setResults] = useState<{ questionNumber: number; selected: string; correct: string; isCorrect: boolean }[]>([]);

  // Restore progress from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const progress = JSON.parse(saved);
        if (progress.results?.length > 0) {
          setResults(progress.results);
          setCurrentQuestion(progress.currentQuestion ?? progress.results.length);
          setQuizStarted(true);
          setStartTime(progress.startTime || Date.now());
          if (progress.quizFinished) {
            setQuizFinished(true);
          }
        }
      }
    } catch { /* ignore parse errors */ }
  }, [storageKey]);

  // Save progress to localStorage whenever results change
  useEffect(() => {
    if (results.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify({
        results,
        currentQuestion,
        startTime,
        quizFinished,
      }));
    }
  }, [results, currentQuestion, quizFinished, startTime, storageKey]);

  // Fetch quiz WITH answers (we use a special endpoint or include answers)
  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setLoading(true);
        // Fetch quiz with answers included for instant feedback
        const data = await api.get(`/quiz/${quizSlug}/full`);
        setQuiz(data);
      } catch (err: any) {
        // Fallback to regular endpoint if /full doesn't exist
        try {
          const data = await api.get(`/quiz/${quizSlug}`);
          setQuiz(data);
        } catch (err2: any) {
          setError(err2.message || 'Failed to load quiz');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchQuiz();
  }, [quizSlug]);

  const startQuiz = () => {
    setQuizStarted(true);
    if (!startTime) setStartTime(Date.now());
  };

  const handleAnswerSelect = (label: string) => {
    if (answered) return; // Already answered this question
    setSelectedAnswer(label);
    setAnswered(true);

    const question = quiz!.questions[currentQuestion];
    const isCorrect = question.correctAnswer === label;

    setResults(prev => [...prev, {
      questionNumber: question.questionNumber,
      selected: label,
      correct: question.correctAnswer || '',
      isCorrect,
    }]);
  };

  const nextQuestion = () => {
    if (quiz && currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setAnswered(false);
    } else {
      // Last question — finish quiz
      finishQuiz();
    }
  };

  const [savedToDB, setSavedToDB] = useState<boolean | null>(null); // null = not attempted, true = saved, false = failed

  const finishQuiz = async () => {
    setQuizFinished(true);

    // Submit to backend for recording
    if (state.isAuthenticated && quiz) {
      const timeTaken = Math.round((Date.now() - startTime) / 1000);
      try {
        await api.post('/quiz/submit', {
          quizSlug,
          answers: results.map(r => ({ questionNumber: r.questionNumber, selectedAnswer: r.selected })),
          timeTaken,
        });
        setSavedToDB(true);
      } catch (err) {
        console.error('Failed to record quiz attempt:', err);
        setSavedToDB(false);
      }
    }
  };

  const resetQuiz = () => {
    localStorage.removeItem(storageKey);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setAnswered(false);
    setQuizStarted(false);
    setQuizFinished(false);
    setResults([]);
    setStartTime(0);
    setSavedToDB(null);
  };

  const score = results.filter(r => r.isCorrect).length;
  const totalQuestions = quiz?.totalQuestions || 0;
  const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;

  const getLevel = () => {
    if (percentage >= 95) return 'System Design Ready';
    if (percentage >= 80) return 'Advanced';
    if (percentage >= 55) return 'Intermediate';
    if (percentage >= 30) return 'Learning';
    return 'Beginner';
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'System Design Ready': return 'text-green-500';
      case 'Advanced': return 'text-blue-500';
      case 'Intermediate': return 'text-yellow-500';
      case 'Learning': return 'text-orange-500';
      default: return 'text-red-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="quiz-loader w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="text-center py-12 text-red-500">
        <p>{error || 'Quiz not found'}</p>
      </div>
    );
  }

  // Start screen
  if (!quizStarted) {
    return (
      <div className="quiz-experience max-w-2xl mx-auto py-8">
        <div className="quiz-card quiz-start-card bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <Brain className="quiz-brand-icon w-16 h-16 text-primary-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 font-display">
              {quiz.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{quiz.description}</p>
            <div className="quiz-summary-meta flex items-center justify-center gap-6 mb-8 text-sm text-gray-600 dark:text-gray-400">
              <span>{quiz.totalQuestions} Questions</span>
              <span>•</span>
              <span>Instant Feedback</span>
            </div>
            <button
              onClick={startQuiz}
              className="quiz-primary-button px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors"
            >
              Start Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Final result screen
  if (quizFinished) {
    const level = getLevel();
    const timeTaken = Math.round((Date.now() - startTime) / 1000);

    return (
      <div className="quiz-experience max-w-3xl mx-auto py-8">
        <div className="quiz-card quiz-result-card bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <div className="text-center mb-8">
            <Trophy className={`w-16 h-16 mx-auto mb-4 ${getLevelColor(level)}`} />
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {score}/{totalQuestions}
            </h2>
            <p className={`text-xl font-semibold ${getLevelColor(level)}`}>{level}</p>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {percentage}% correct • {Math.floor(timeTaken / 60)}m {timeTaken % 60}s
            </p>
          </div>

          {/* Score bar */}
          <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded-full mb-6">
            <div
              className={`h-4 rounded-full transition-all duration-500 ${percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${percentage}%` }}
            />
          </div>

          <button
            onClick={resetQuiz}
            className="quiz-primary-button flex items-center gap-2 mx-auto px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" /> Retake Quiz
          </button>

          {/* Save status */}
          {savedToDB === true && (
            <p className="text-center text-sm text-green-500 mt-3">✅ Results saved to your profile</p>
          )}
          {savedToDB === false && (
            <p className="text-center text-sm text-yellow-500 mt-3">⚠️ Could not save results (sign in with a real account to track progress)</p>
          )}
        </div>

        {/* Review all answers */}
        <div className="space-y-3">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Review</h3>
          {results.map((r, idx) => {
            const question = quiz.questions[idx];
            return (
              <div key={idx} className={`quiz-review-item ${r.isCorrect ? 'is-correct' : 'is-incorrect'} p-4 rounded-lg border ${r.isCorrect ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'}`}>
                <div className="flex items-start gap-3">
                  {r.isCorrect ? <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">Q{r.questionNumber}. {question?.question}</p>
                    <p className="text-sm mt-1">
                      <span className="text-gray-500">Your answer: </span>
                      <span className={r.isCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>{r.selected}</span>
                      {!r.isCorrect && <span className="text-green-600 dark:text-green-400 ml-2">(Correct: {r.correct})</span>}
                    </p>
                    {question?.explanation && <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">{question.explanation}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Question screen with instant feedback
  const question = quiz.questions[currentQuestion];
  const isCorrect = selectedAnswer === question.correctAnswer;

  return (
    <div className="quiz-experience max-w-2xl mx-auto py-8">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
          <span>Question {currentQuestion + 1} of {quiz.totalQuestions}</span>
          <span className="flex items-center gap-2">
            <span className="text-green-500">{results.filter(r => r.isCorrect).length} ✓</span>
            <span className="text-red-500">{results.filter(r => !r.isCorrect).length} ✗</span>
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
          <div
            className="quiz-progress-fill h-2 bg-primary-600 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / quiz.totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="quiz-card quiz-question-card bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
          {question.question}
        </h3>

        <div className="space-y-3">
          {question.options.map((option) => {
            const isSelected = selectedAnswer === option.label;
            const isCorrectOption = question.correctAnswer === option.label;

            let optionStyle = 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300';

            if (answered) {
              if (isCorrectOption) {
                optionStyle = 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300';
              } else if (isSelected && !isCorrect) {
                optionStyle = 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300';
              } else {
                optionStyle = 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-500 opacity-60';
              }
            } else if (isSelected) {
              optionStyle = 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300';
            }

            return (
              <button
                key={option.label}
                onClick={() => handleAnswerSelect(option.label)}
                disabled={answered}
                className={`quiz-option ${answered && isCorrectOption ? 'is-correct' : ''} ${answered && isSelected && !isCorrect ? 'is-incorrect' : ''} ${answered && !isCorrectOption && !isSelected ? 'is-muted' : ''} w-full text-left p-4 rounded-lg border-2 transition-all ${optionStyle} ${answered ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-semibold">{option.label}.</span>
                  <span className="flex-1">{option.text}</span>
                  {answered && isCorrectOption && <CheckCircle className="w-5 h-5 text-green-500" />}
                  {answered && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500" />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Feedback after answering */}
        {answered && (
          <div className={`quiz-feedback ${isCorrect ? 'is-correct' : 'is-incorrect'} mt-6 p-4 rounded-lg ${isCorrect ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 mb-1">
              {isCorrect ? (
                <><CheckCircle className="w-5 h-5 text-green-500" /><span className="font-semibold text-green-700 dark:text-green-300">Correct!</span></>
              ) : (
                <><XCircle className="w-5 h-5 text-red-500" /><span className="font-semibold text-red-700 dark:text-red-300">Incorrect</span></>
              )}
            </div>
            {question.explanation && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{question.explanation}</p>
            )}
            {!isCorrect && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                Correct answer: <strong>{question.correctAnswer}</strong>
              </p>
            )}
          </div>
        )}
      </div>

      {/* Next button (only after answering) */}
      {answered && (
        <div className="flex justify-end">
          <button
            onClick={nextQuestion}
            className="quiz-primary-button flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors"
          >
            {currentQuestion < quiz.questions.length - 1 ? (
              <>Next Question <ArrowRight className="w-4 h-4" /></>
            ) : (
              <>View Results <Trophy className="w-4 h-4" /></>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default QuizComponent;
